<template>
  <div class="animated fadeIn">
    <div class="container py-4">
      <!-- Header Section with Back Link -->
      <div class="row">
        <div class="col-12">
          <a href="#" @click.prevent="navigateBack" class="text-decoration-none d-inline-block mb-3">
            <font-awesome-icon icon="chevron-left" class="mr-2" />
            <span>{{ $t('Webportal.Actions.InquiryWizard.Landing.BackToOverview') }}</span>
          </a>
          <div class="text-center mt-3 mb-5">
            <h2 class="mb-2">{{ $t('Webportal.Actions.InquiryWizard.Landing.Title') }}</h2>
            <p class="text-muted">
              {{ $t('Webportal.Actions.InquiryWizard.Landing.Subtitle') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Check Information Panel -->
      <div v-if="showInfoPanel" class="row mb-4">
        <div class="col-12">
          <b-alert show variant="warning" class="mb-0 d-flex align-items-start">
            <div>
              <div
                class="icon-circle rounded-circle d-inline-flex align-items-center justify-content-center rounded mb-3 mr-3"
                style="background-color: rgba(255, 193, 7, 1)"
              >
                <font-awesome-icon :icon="['fal', 'exclamation-circle']" size="lg" />
              </div>
            </div>
            <div>
              <h6 class="alert-heading">
                {{ $t('Webportal.Actions.InquiryWizard.Landing.NewReports', { count: summaryData.UnreadCheckCount }) }}
                <b-badge variant="danger">{{ $t('Webportal.New') }}</b-badge>
              </h6>
              <p class="mb-0 mt-2 small">
                {{ $t('Webportal.Actions.InquiryWizard.Landing.NewReportsHint') }}
                <span v-if="summaryData.RevisionMaxDate">
                  {{ $t('Webportal.Actions.InquiryWizard.Landing.DeadlineHint', { days: summaryData.SurveyRevisionCreationLimitInDays }) }}
                </span>
              </p>
            </div>
          </b-alert>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="row">
        <!-- Left Card: General Inquiry -->
        <div class="col-12 col-md-6 mb-3">
          <b-card
            class="h-100 border-0 shadow-sm inquiry-card-general clickable"
            body-class="d-flex flex-column"
            @click="handleGeneralInquiry"
          >
            <div class="mt-3">
              <div
                class="icon-square d-inline-flex align-items-center justify-content-center rounded mb-3"
                style="background-color: #e0e0e0"
              >
                <font-awesome-icon :icon="['fal', 'exclamation-circle']" size="2x" />
              </div>
            </div>
            <h4 class="mb-3">{{ $t('Webportal.Actions.InquiryWizard.Landing.GeneralCard.Title') }}</h4>
            <p class="text-muted mb-3">{{ $t('Webportal.Actions.InquiryWizard.Landing.GeneralCard.Description') }}</p>
            <ul class="list-unstyled mb-4 mt-auto">
              <li class="mb-2 d-flex align-items-start">
                <font-awesome-icon icon="check" class="text-success mr-2 mt-1 fixed-width-icon" />
                <span>{{ $t('Webportal.Actions.InquiryWizard.Landing.GeneralCard.Feature1') }}</span>
              </li>
              <li class="mb-2 d-flex align-items-start">
                <font-awesome-icon icon="check" class="text-success mr-2 mt-1 fixed-width-icon" />
                <span>{{ $t('Webportal.Actions.InquiryWizard.Landing.GeneralCard.Feature2') }}</span>
              </li>
              <li class="mb-2 d-flex align-items-start">
                <font-awesome-icon icon="check" class="text-success mr-2 mt-1 fixed-width-icon" />
                <span>{{ $t('Webportal.Actions.InquiryWizard.Landing.GeneralCard.Feature3') }}</span>
              </li>
            </ul>
            <b-button variant="light" block class="mt-auto">
              {{ $t('Webportal.Actions.InquiryWizard.Landing.GeneralCard.Button') }}
              <font-awesome-icon icon="chevron-right" class="ml-2" />
            </b-button>
          </b-card>
        </div>

        <!-- Right Card: Report Inquiry -->
        <div class="col-12 col-md-6 mb-3">
          <b-card
            class="h-100 shadow inquiry-card-report clickable"
            border-variant="white"
            body-class="d-flex flex-column"
            @click="handleReportInquiry"
          >
            <div class="mt-3">
              <div
                class="icon-square text-white d-inline-flex align-items-center justify-content-center rounded mb-3"
                style="background-color: rgba(255, 255, 255, 0.3)"
              >
                <font-awesome-icon icon="star" size="2x" />
              </div>
            </div>
            <h4 class="text-white mb-3">{{ $t('Webportal.Actions.InquiryWizard.Landing.ReportCard.Title') }}</h4>
            <p class="text-white-50 mb-3">{{ $t('Webportal.Actions.InquiryWizard.Landing.ReportCard.Description') }}</p>
            <ul class="list-unstyled text-white mb-4 mt-auto">
              <li class="mb-2 d-flex align-items-start">
                <font-awesome-icon icon="check" class="text-white mr-2 mt-1 fixed-width-icon" />
                <span>{{ $t('Webportal.Actions.InquiryWizard.Landing.ReportCard.Feature1') }}</span>
              </li>
              <li class="mb-2 d-flex align-items-start">
                <font-awesome-icon icon="check" class="text-white mr-2 mt-1 fixed-width-icon" />
                <span>{{ $t('Webportal.Actions.InquiryWizard.Landing.ReportCard.Feature2') }}</span>
              </li>
              <li class="mb-2 d-flex align-items-start">
                <font-awesome-icon icon="check" class="text-white mr-2 mt-1 fixed-width-icon" />
                <span>{{ $t('Webportal.Actions.InquiryWizard.Landing.ReportCard.Feature3') }}</span>
              </li>
            </ul>
            <b-button variant="light" block class="mt-auto">
              {{ $t('Webportal.Actions.InquiryWizard.Landing.ReportCard.Button') }}
              <font-awesome-icon icon="chevron-right" class="ml-2" />
            </b-button>
          </b-card>
        </div>
      </div>

      <!-- Bottom Information Hint -->
      <div class="row mt-4">
        <div class="col-12">
          <b-alert show variant="" class="border mb-0 d-flex align-items-start" style="border-color: lightgray !important">
            <div>
              <div
                class="icon-circle rounded-circle d-inline-flex align-items-center justify-content-center rounded mb-3 mr-3"
                style="background-color: #d1ecf1"
              >
                <font-awesome-icon class="text-info" :icon="['fal', 'exclamation-circle']" size="lg" />
              </div>
            </div>
            <div>
              <h6 class="alert-heading">{{ $t('Webportal.Actions.InquiryWizard.Landing.InfoBox.Title') }}</h6>
              <p class="mb-0 text-muted small">
                {{ $t('Webportal.Actions.InquiryWizard.Landing.InfoBox.Description') }}
              </p>
            </div>
          </b-alert>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ActionService from "@/services/ActionService";

export default {
  name: "InquiryLanding",

  data() {
    return {
      summaryData: {
        NewCount: 0,
        InProgressCount: 0,
        CompletedThisWeekCount: 0,
        UnreadCheckCount: 0,
        RevisionMaxDate: null,
        SurveyRevisionCreationLimitInDays: null,
      },
      isLoading: false,
    };
  },

  computed: {
    showInfoPanel() {
      return this.summaryData.UnreadCheckCount > 0;
    },
  },

  created() {
    this.loadSummaryData();
  },

  methods: {
    navigateBack() {
      this.$router.push({ name: "SurveyRevisionExplorer" });
    },

    handleGeneralInquiry() {
      this.$router.push({ name: "GeneralInquiry" });
    },

    handleReportInquiry() {
      this.$store.dispatch("inquiryWizard/initializeWizard", "report");
      this.$router.push({ name: "InquiryWizard" });
    },

    async loadSummaryData() {
      this.isLoading = true;
      try {
        const response = await ActionService.getSurveyRevisionsSummary();
        this.summaryData = response.data;

        // Store SurveyRevisionCreationLimitInDays in inquiryWizard store
        if (this.summaryData.SurveyRevisionCreationLimitInDays) {
          this.$store.dispatch("inquiryWizard/setSurveyRevisionCreationLimitInDays", this.summaryData.SurveyRevisionCreationLimitInDays);
        }
      } catch (error) {
        console.error("Error loading summary data:", error);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.clickable {
  cursor: pointer;
}

.icon-circle {
  width: 42px;
  height: 42px;
}

.icon-square {
  width: 64px;
  height: 64px;
}

.fixed-width-icon {
  width: 1em;
  text-align: center;
  flex-shrink: 0;
}

.inquiry-card-general {
  background-color: var(--light);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-4px);
  }
}

.inquiry-card-report {
  background-color: var(--primary);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-4px);
  }
}
</style>
